/**
 * <gamba-collection-progress> — la barra de progreso del riel de collection.
 *
 * Abajo de 990px `snippets/gamba-collection-styles.liquid` convierte el `ul`
 * de la grilla de Horizon en un carrusel con scroll-snap. El boceto le pone
 * abajo una barra de 2px cuyo thumb ocupa la fracción visible y se desplaza
 * sobre el resto (`Gamba%20Mobile.dc.html:452-454`).
 *
 * NO reusa `gamba-rail`: ese componente trae su propio markup de track, y acá
 * el track es el `ul` que renderiza Horizon, que tiene que seguir siendo el
 * mismo nodo para que los facets, la paginación y el section rendering
 * funcionen igual (decisión D-3 del plan de la fase 2).
 *
 * Los atributos que emite son NEGATIVOS a propósito ([not-scrollable]):
 * `morph.js` borra del nodo vivo todo atributo que no venga del servidor, y con
 * esa polaridad la pérdida degrada a permisivo —la barra se muestra— en vez de
 * esconderla para siempre. Es la misma lección de la corrección 32.
 */
class GambaCollectionProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {number} */
  #frame = 0;

  connectedCallback() {
    // El track es el `ul` de la grilla de la MISMA sección: `results-list` es
    // el nodo que `results-list.js` reemplaza al filtrar, así que acota bien.
    const scope = this.closest('results-list') ?? this.closest('.gamba-collection');
    const track = scope?.querySelector('ul.product-grid');

    this.#track = track instanceof HTMLElement ? track : null;
    if (!this.#track) return;

    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.sync();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  get #thumb() {
    return this.querySelector('[data-progress-thumb]');
  }

  /**
   * Mide el track y escribe el thumb. Se mide TODO primero y se escribe
   * después: alternar lectura y escritura fuerza un reflow sincrónico.
   */
  sync() {
    const track = this.#track;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    // 1px de tolerancia: el subpíxel de un track de ancho fraccionario deja un
    // maxScroll de 0,5px que no es scroll de verdad.
    const scrollable = maxScroll > 1;

    // Un track que scrollea es una región scrolleable, y una región scrolleable
    // tiene que ser alcanzable por teclado. Lo escribe el JS porque el `ul` lo
    // renderiza un snippet de Horizon compartido con la búsqueda, donde el
    // track NO scrollea. Si `morph.js` se lo lleva al filtrar, el próximo
    // connect lo vuelve a poner.
    if (scrollable) {
      if (track.getAttribute('tabindex') !== '0') track.setAttribute('tabindex', '0');
      if (!track.hasAttribute('aria-label')) {
        const label = this.getAttribute('track-label');
        if (label) track.setAttribute('aria-label', label);
      }
    } else {
      track.removeAttribute('tabindex');
    }

    this.toggleAttribute('not-scrollable', !scrollable);
    if (!scrollable) return;

    const visible = track.clientWidth / track.scrollWidth;
    const progress = track.scrollLeft / maxScroll;
    const width = Math.max(8, Math.min(100, visible * 100));
    const nextWidth = `${width}%`;
    const nextOffset = `${progress * (100 - width)}%`;

    const thumb = this.#thumb;
    if (!(thumb instanceof HTMLElement)) return;

    if (thumb.style.width !== nextWidth) thumb.style.width = nextWidth;
    if (thumb.style.marginInlineStart !== nextOffset) thumb.style.marginInlineStart = nextOffset;
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.sync();
    });
  };
}

if (!customElements.get('gamba-collection-progress')) {
  customElements.define('gamba-collection-progress', GambaCollectionProgress);
}
